import React from "react";

import {
  CityConfig,
  CityConfigService,
  CityConfigSwagger,
  Sector,
  SwaggerCityConfigDao,
  Zone,
} from "@egis-smartcity/parking";
import CityConfigContext, { CityConfigState } from "./CityConfigContext";
import useAuth from "../../hooks/useAuth";
import { PARKING_API_ENDPOINT } from "../../utils/secrets";

export type CityConfigProviderProps = React.PropsWithChildren<{}>;

function CityConfigProvider(props: CityConfigProviderProps) {
  const { children } = props;
  const { cityId, token } = useAuth();

  const cityConfigService: CityConfigService | undefined = React.useMemo(() => {
    if (!token) return;

    const cityApiEndpoint = process.env.REACT_APP_CITY_API_ENDPOINT;
    if (cityApiEndpoint === undefined)
      throw new Error("missing key 'REACT_APP_CITY_API_ENDPOINT' in dotenv");
    const citySwaggerClient = new CityConfigSwagger.CityApi(
      new CityConfigSwagger.Configuration({
        basePath: cityApiEndpoint,
        apiKey: `Bearer ${token}`,
      })
    );

    if (PARKING_API_ENDPOINT === undefined)
      throw new Error("missing key 'REACT_APP_PARKING_API_ENDPOINT' in dotenv");
    const parkingSwaggerClient = new CityConfigSwagger.ParkingApi(
      new CityConfigSwagger.Configuration({
        basePath: PARKING_API_ENDPOINT,
        apiKey: `Bearer ${token}`,
      })
    );
    const swaggerCityConfigDao = new SwaggerCityConfigDao(
      citySwaggerClient,
      parkingSwaggerClient
    );

    return new CityConfigService(swaggerCityConfigDao);
  }, [token]);
  const [cityConfig, setCityConfig] = React.useState<CityConfig>();
  const [sectors, setSectors] = React.useState<Sector[]>();
  const [zones, setZones] = React.useState<Zone[]>();

  React.useEffect(() => {
    let mounted = true;

    if (!cityConfigService) return;

    cityConfigService
      .getCityConfig()
      .then((cityConfig) => {
        if (mounted) setCityConfig(cityConfig);
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") console.error(error);
      });

    return () => {
      mounted = false;
    };
  }, [cityConfigService]);

  React.useEffect(() => {
    let mounted = true;

    if (!cityConfigService || !cityId) return;

    cityConfigService
      .getSectors(cityId)
      .then((sectors) => {
        if (mounted) setSectors(sectors);
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") console.error(error);
      });

    cityConfigService
      .getZones(cityId)
      .then((zones) => {
        if (mounted) setZones(zones);
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") console.error(error);
      });

    return () => {
      mounted = false;
    };
  }, [cityConfigService, cityId]);

  const cityConfigState: CityConfigState = React.useMemo(() => {
    return { cityConfigService, cityConfig, sectors, zones };
  }, [cityConfig, cityConfigService, sectors, zones]);

  return (
    <CityConfigContext.Provider value={cityConfigState}>
      {children}
    </CityConfigContext.Provider>
  );
}

export default CityConfigProvider;
